import {
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  darken,
  FormControlLabel,
  LinearProgress,
  linearProgressClasses,
  MenuItem,
  Radio,
  RadioGroup,
  styled,
  Switch,
  SwitchProps,
  SxProps,
  Theme,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from '@mui/material';
import TextField from '~/components/Form/TextField/TextField';
import theme from '~/theme';

export const summaryBoxStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: 1,
  padding: 2,
  backgroundColor: theme.palette.grey[50],
};

export const supplyManagerRulesDialogTabsSx: SxProps<Theme> = {
  minHeight: 40,
  '& .MuiTabs-flexContainer': {
    gap: 0.5,
  },
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.dianomiGreen.main,
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
  '& .MuiTab-root': {
    textTransform: 'capitalize',
    fontWeight: 500,
    fontSize: '14px',
    minHeight: 40,
    px: 2,
    py: 1,
    color: 'text.secondary',
    borderRadius: '5px 5px 0 0',
    transition: 'color 0.2s ease, background-color 0.2s ease',
    '&.Mui-selected': {
      color: theme.palette.dianomiGreen.main,
      fontWeight: 600,
      backgroundColor: alpha(theme.palette.dianomiGreen.main, 0.08),
    },
    '&:hover': {
      backgroundColor: alpha(theme.palette.dianomiGreen.main, 0.04),
    },
  },
};

export const SupplyManagerOutlinedButton = styled(Button)(() => ({
  textTransform: 'capitalize',
  backgroundColor: 'white',
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
  border: `1px solid ${theme.palette.grey[300]}`,
  ...theme.applyStyles('dark', {
    border: `1px solid ${theme.palette.grey[700]}`,
  }),
}));

SupplyManagerOutlinedButton.defaultProps = {
  variant: 'outlined',
  color: 'primary',
};

export const SupplyManagerFilterSelectField = styled(TextField)(() => ({
  minWidth: 160,
  maxWidth: 180,
  margin: 0,
  '& .MuiInputBase-root': {
    backgroundColor: theme.palette.common.white,
    borderRadius: '5px',
  },
  '& .MuiInputBase-input': {
    py: 0.75,
    px: 1.25,
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    color: theme.palette.grey[600],
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[300],
  },
  '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[400],
  },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.dianomiGreen.main,
    borderWidth: 1,
  },
  '& .MuiSelect-icon': {
    fontSize: 18,
    color: theme.palette.grey[600],
  },
}));

export const SupplyManagerFilterSelectMenuItem = styled(MenuItem)(() => ({
  fontSize: '12px',
  minHeight: 32,
  py: 0.5,
  px: 1.5,
  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.dianomiGreen.main, 0.08),
    color: theme.palette.dianomiGreen.main,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: alpha(theme.palette.dianomiGreen.main, 0.12),
    },
  },
  '&:hover': {
    backgroundColor: theme.palette.grey[50],
  },
}));

export const supplyManagerFilterSelectMenuProps = {
  anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
  transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
  PaperProps: {
    sx: {
      borderRadius: '5px',
      border: `1px solid ${theme.palette.grey[200]}`,
      boxShadow: theme.shadows[4],
      mt: 0.5,
      maxHeight: 280,
      '& .MuiList-root': {
        py: 0.5,
      },
    },
  },
};

export const SupplyManagerInfoChip = styled(Chip)(() => ({
  borderRadius: '5px',
  padding: '7px 15px',
  backgroundColor: 'white',
  border: `1px solid ${theme.palette.grey[200]}`,
  '& .MuiSvgIcon-root.MuiChip-icon': {
    color: theme.palette.dianomiGreen.main,
  },
}));

export const SupplyManagerCheckBox = styled(Checkbox)(() => ({
  color: theme.palette.grey[700],
  '&.Mui-checked': {
    color: theme.palette.dianomiGreen.main,
  },
}));

export const SupplyManagerRadio = styled(Radio)(() => ({
  padding: theme.spacing(0.75),
  color: theme.palette.grey[500],
  '&.Mui-checked': {
    color: theme.palette.dianomiGreen.main,
  },
}));

export const UnblockOptionRadioGroup = styled(RadioGroup)(() => ({
  gap: theme.spacing(1),
}));

export const UnblockOptionField = styled(FormControlLabel)(() => ({
  margin: 0,
  alignItems: 'flex-start',
  padding: '10px 12px',
  borderRadius: '5px',
  border: `1px solid ${theme.palette.grey[300]}`,
  backgroundColor: theme.palette.common.white,
  transition: 'border-color 0.2s ease, background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.grey[50],
  },
  '&:has(.MuiRadio-root.Mui-checked)': {
    borderColor: theme.palette.dianomiGreen.main,
    backgroundColor: alpha(theme.palette.dianomiGreen.main, 0.06),
  },
  '&.Mui-disabled': {
    opacity: 0.55,
    backgroundColor: theme.palette.grey[50],
  },
  '& .MuiFormControlLabel-label': {
    marginTop: '1px',
  },
}));

export const UnblockOptionTitle = styled(Typography)(() => ({
  display: 'block',
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: 1.35,
  color: theme.palette.text.primary,
}));

export const UnblockOptionDescription = styled(Typography)(() => ({
  display: 'block',
  fontSize: '11px',
  lineHeight: 1.45,
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.25),
}));

export const UnblockOptionPanel = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5),
  borderRadius: '5px',
  border: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: theme.palette.grey[50],
}));

export const UnblockOptionPanelHeading = styled(Typography)(() => ({
  fontSize: '12px',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}));

export const SupplyManagerOutlinedBox = styled(Box)(() => ({
  backgroundColor: 'white',
  '&.MuiBox-root': {
    padding: '10px 15px',
    fontSize: '14px',
  },
  borderRadius: '5px',
  border: `1px solid ${theme.palette.grey[300]}`,
  ...theme.applyStyles('dark', {
    border: `1px solid ${theme.palette.grey[700]}`,
  }),
}));

export const SupplyManagerSummaryBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: 1,
  padding: 2,
  backgroundColor: theme.palette.grey[50],
}));

interface SupplyManagerSwitchProps extends SwitchProps {
  redOnChecked?: boolean;
}

const redOnCheckedColor = '#CC3737';
const greenOnCheckedColor = '#368943';

export const SupplyManagerSwitch = styled((props: SupplyManagerSwitchProps) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ checked }) => ({
  width: 36,
  height: 20,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: redOnCheckedColor,
        opacity: 1,
        border: 0,
        ...theme.applyStyles('dark', {
          backgroundColor: redOnCheckedColor,
        }),
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.1,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: checked ? redOnCheckedColor : greenOnCheckedColor,
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      opacity: 0.8,
      color: theme.palette.grey[400],
      ...theme.applyStyles('dark', {
        color: theme.palette.grey[400],
      }),
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.8,
      color: theme.palette.grey[400],
      ...theme.applyStyles('dark', {
        opacity: 0.3,
      }),
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 16,
    height: 16,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
    ...theme.applyStyles('dark', {
      backgroundColor: '#39393D',
    }),
  },
}));

export const SupplyManagerLinearProgress = styled(LinearProgress)(() => ({
  height: 12,
  borderRadius: 5,
  width: '90%',
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar1}`]: {
    backgroundImage: `linear-gradient(90deg, ${darken('#437e84', 0.5)} 0%, ${
      theme.palette.dianomiGreen.main
    } 100%)`,
  },
  [`& .${linearProgressClasses.bar2}`]: {
    backgroundColor: '#00D36D',
  },
  [`& .${linearProgressClasses.dashed}`]: {
    display: 'none',
  },
}));

export const SupplyManagerGenericTooltip = styled(({ className, ...props }: TooltipProps) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    padding: '0px',
  },
}));

export const BulkActionsInfoContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  gap: '16px',
  fontWeight: '400',
});

export const BulkActionsDetailBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const iconBoxSx = (hasExceededBlockLimit: boolean) => ({
  width: '50px',
  height: '50px',
  borderRadius: '50%',
  backgroundColor: hasExceededBlockLimit
    ? alpha(theme.palette.error.dark, 0.1)
    : alpha(theme.palette.dianomiGreen.main, 0.1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const bulkCapacityChipIconSx = {
  fontSize: '10px',
} as const;

export const bulkSelectionChipSx: SxProps<Theme> = {
  width: '100%',
  '& .MuiSvgIcon-root.MuiChip-icon': {
    color: theme.palette.grey[800],
  },
};

export const bulkCapacityChipSuccessSx: SxProps<Theme> = {
  borderRadius: '20px',
  fontSize: '10px',
  borderColor: 'dianomiGreen.main',
  backgroundColor: 'dianomiGreen.main',
  color: 'white',
  '& .MuiSvgIcon-root.MuiChip-icon': {
    ...bulkCapacityChipIconSx,
    color: 'white',
  },
};

export const bulkCapacityChipErrorSx: SxProps<Theme> = {
  borderRadius: '20px',
  fontSize: '10px',
  borderColor: 'error.light',
  backgroundColor: 'error.light',
  color: 'white',
  '& .MuiSvgIcon-root.MuiChip-icon': {
    ...bulkCapacityChipIconSx,
    color: 'white',
  },
};

export const bulkCapacityChipNeutralSx: SxProps<Theme> = {
  borderRadius: '20px',
  fontSize: '10px',
  borderColor: 'grey.400',
  backgroundColor: 'grey.200',
  color: theme.palette.grey[800],
  '& .MuiSvgIcon-root.MuiChip-icon': {
    ...bulkCapacityChipIconSx,
    color: theme.palette.grey[800],
  },
};

export const RuleCardWrapperBox = styled(Box)(() => ({
  flexDirection: 'row',
  padding: '15px',
  gap: '8px',
  display: 'flex',
  width: '100%',
  border: `1.5px solid ${alpha(theme.palette.common.black, 0.05)}`,
  borderRadius: '5px',
}));

export const RuleCardContainerBox = styled(Box)(() => ({
  flexDirection: 'column',
  gap: '8px',
  display: 'flex',
}));

export const RuleCardInfoSectionBox = styled(Box)(() => ({
  flexDirection: 'row',
  justifyContent: 'flex-start',
  display: 'flex',
  gap: '24px',
}));

export const RuleCardChip = styled(Chip)(() => ({
  textTransform: 'capitalize',
  borderRadius: '5px',
  width: 'fit-content',
  height: 'fit-content',
  padding: '2px 20px',
  '& .MuiChip-label': {
    padding: '0px',
  },
  backgroundColor: alpha(theme.palette.dianomiGreen.main, 0.1),
  color: theme.palette.dianomiGreen.main,
  fontSize: '10px',
}));

export const ConflictCardBox = styled(Box)(() => ({
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
}));

// styles for the supply manager page
